import React, { useEffect, useRef, useState } from 'react';
import './types';
import { Spinner } from '@nykaa/ui-components/Loader';
import { Loader, Wrapper, FindMyShadeButton } from './styled';
import { MMMProduct } from './types';
import { FIND_SHADE_BTN_ID, SKU_IDS } from './constants';
import {
  closeMMMWidget,
  doAddToCartViaApp,
  fireFlowCompletionEvent,
  fireFlowInitiatedEvent,
  fireScriptFailEvent,
  fireScriptLoadCompletionEvent,
} from './appCallbacks';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { logger } from '@nykaa/logger';

export default function FoundationFinder() {
  const [loading, setLoading] = useState(false);
  const [skuIds, setSkuIds] = useState('');
  const device = useSelector((state: State) => state.appReducer?.client.device);
  const isJourneyComplete = useRef(false);
  const journeyType = useRef('');
  const MMM_CLIENT_KEY = useSelector(
    (state: State) => state.remoteConfig.configs.mmmClientKey
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ids = params.get(SKU_IDS) || '';
    setSkuIds(ids);
  }, []);

  useEffect(() => {
    if (!skuIds) return;
    if (!MMM_CLIENT_KEY) return;
    setLoading(true);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://in.matchmymakeup.com/client/${MMM_CLIENT_KEY}/v2/widget.css`;

    const script = document.createElement('script');
    script.type = 'module';
    script.src = `https://in.matchmymakeup.com/client/${MMM_CLIENT_KEY}/v2/init.js`;

    const style = document.createElement('style');
    style.innerHTML = `
      #mmm-widget-frame {
        max-height: 100vh !important;
        max-width: 100vw !important;
      }
    `;
    document.head.appendChild(style);

    const attachCallbacks = () => {
      document.MMMWidget = document.MMMWidget || {};
      document.MMMWidget.insights = document.MMMWidget.insights || {};

      document.MMMWidget.insights.onWidgetOpen = () => {
        isJourneyComplete.current = false;
      };

      document.MMMWidget.insights.onJourneyEvent = ({ detail }) => {
        const { journey_event } = detail || {};
        journeyType.current = journey_event || '';
        fireFlowInitiatedEvent(device, journey_event);
      };

      document.MMMWidget.insights.onProductResult = () => {
        isJourneyComplete.current = true;
      };

      document.MMMWidget.insights.onWidgetClose = ({ journeyType }) => {
        closeMMMWidget(device);
        if (isJourneyComplete.current) {
          // fire flow completion event
          fireFlowCompletionEvent(device, journeyType);
        }
      };

      // Add to Cart callback
      document.MMMWidget.addToCart = (product: MMMProduct) => {
        doAddToCartViaApp(product, device, journeyType.current);
      };
    };
    script.onload = () => {
      document.mmm?.init?.();
      attachCallbacks();
      setLoading(false);
      fireScriptLoadCompletionEvent(device);
    };

    script.onerror = () => {
      setLoading(false);
      fireScriptFailEvent(device);
    };

    document.head.appendChild(link);
    document.body.appendChild(script);

    return () => {
      document.mmm?.cleanup?.();
      document.MMMWidget = undefined;
      if (script.parentNode) {
        document.body.removeChild(script);
      }
      if (link.parentNode) {
        document.head.removeChild(link);
      }
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, [skuIds, device, MMM_CLIENT_KEY]);

  useEffect(() => {
    if (!window.NYKAA_WEB_INTERFACE) {
      window.NYKAA_WEB_INTERFACE = {};
    }
    window.NYKAA_WEB_INTERFACE.openMMMWidget = () => {
      const button = document.getElementById(FIND_SHADE_BTN_ID);
      if (button) {
        button.click();
      }
      logger.log('Opening MMM Widget via Web Interface');
    };
    return () => {
      delete window.NYKAA_WEB_INTERFACE.openMMMWidget;
    };
  }, []);

  return (
    <Wrapper>
      {loading && (
        <Loader>
          <Spinner />
        </Loader>
      )}
      <FindMyShadeButton
        data-mmm-action
        data-mmm-native-dropdown
        data-mmm-sku={skuIds}
        id={FIND_SHADE_BTN_ID}
      >
        Find my Shade
      </FindMyShadeButton>
    </Wrapper>
  );
}
